<template>
  <v-combobox :model-value="modelValue"
              :error-messages="categoriesErrors"
              :hide-no-data="false"
              :items="existingCategories"
              :placeholder="$t('project.question-answer.QuestionAnswerCategoriesSelect.placeholder')"
              :persistent-placeholder="true"
              :rules="[checkMaxLength]"
              append-icon=""
              :clearable="true"
              color="blue-darken-2"
              density="compact"
              :hide-selected="true"
              item-value="id"
              item-title="name"
              multiple
              variant="outlined"
              class="categories-select"
              @update:search="onSearchValueChange"
              @update:model-value="onModelValueChange"
              ref="combobox"
  >
    <template #selection="{index}">
      <QuestionAnswerCategoryChip :category="modelValue[index]"
                                  @remove-category="() => removeCategoryAtIndex(index)"
      />
    </template>

   <template v-if="searchValue && searchValue.trim().length > 0" #no-data>
     <div class="px-5 py-2 d-flex align-center" @click="createTemporaryCategory">
       <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-3 text-grey-600" />
       <div class="text-grey-800">
        {{ $t('common.add') }} : <span class="font-weight-bold">{{ searchValue }}</span>
       </div>
     </div>
   </template>

   <template v-if="optionToCreateCategoryIsDisplayed" #append-item>
     <div class="px-5 py-2 d-flex align-center" @click="createTemporaryCategory">
       <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-3 text-grey-600" />
       <div class="text-grey-800">
        {{ $t('common.add') }} : <span class="font-weight-bold">{{ searchValue }}</span>
       </div>
      </div>
    </template>
  </v-combobox>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import QuestionAnswerCategoryChip from '@/project/question-answer/QuestionAnswerCategoryChip.vue'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

const CATEGORY_MAX_LENGTH = 100

export default {
  name: 'QuestionAnswerCategoriesSelect',
  components: { QuestionAnswerCategoryChip },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:model-value'],
  validations () {
    return {
      categories: {
        required,
      },
    }
  },
  data () {
    return {
      searchValue: '',
    }
  },
  computed: {
    ...mapState('questionAnswer', {
      existingCategories: state => state.categories,
    }),
    ...mapGetters('room', ['roomMnemo']),
    optionToCreateCategoryIsDisplayed () {
      return this.searchValue.trim().length > 0 &&
        !this.existingCategories.find(category => category.name.toLocaleLowerCase() === this.searchValue.toLocaleLowerCase()) &&
        this.existingCategories.some(category => category.name.toLocaleLowerCase().indexOf(this.searchValue.toLocaleLowerCase()) > -1)
    },
    // computed properties is used here to be used within vuelidate validations
    categories () {
      return this.modelValue
    },
    categoriesErrors () {
      return formFieldErrors(
        this.v$.categories,
        [
          [
            'required',
            this.$t('common.validations.fieldRequired', {
              fieldName: this.$t('project.question-answer.QuestionAnswerCategoriesSelect.categories'),
            }),
          ],
        ],
      )
    },
  },
  created () {
    this.getCategories()
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.GET_CATEGORIES]),
    async getCategories () {
      try {
        this.GET_CATEGORIES(this.roomMnemo)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.question-answer.QuestionAnswerCategoriesSelect.getCategoriesError'))
      }
    },
    removeCategoryAtIndex (index) {
      const currentCategories = [...this.modelValue]
      currentCategories.splice(index, 1)

      this.$emit('update:model-value', currentCategories)
    },
    checkMaxLength (values) {
      for (let i = 0; i < values.length; i++) {
        if (values[i].name.length > CATEGORY_MAX_LENGTH) {
          this.removeCategoryAtIndex(i)
          toast.error(this.$t('project.question-answer.QuestionAnswerCategoriesSelect.categoryTooLong', {
            maxLength: CATEGORY_MAX_LENGTH,
          }))
          return false
        }
      }
      return true
    },
    createTemporaryCategory () {
      if (!this.searchValue) {
        return
      }

      const tempCategory = {
        id: 0,
        name: this.searchValue,
      }

      this.$emit('update:model-value', [...this.modelValue, tempCategory])
      // Reset the combobox internal value. We can't control it directly otherwise
      // We only have an _event_ for value changes.
      this.$refs.combobox.search = ''
    },
    onModelValueChange (categories) {
      const formatTempCategoryFn = (category) => {
        if (category.hasOwnProperty('id')) {
          return category
        }
        // If someone freely types a category that exists, take its existing id
        const existingCategoryId = this.existingCategories.find((existingCategory) => existingCategory.name === category)?.id
        const categoryId = existingCategoryId ?? 0 

        return {
          id: categoryId,
          name: category,
        }
      }

      this.$emit('update:model-value', categories.map(formatTempCategoryFn))
    },
    onSearchValueChange (newSearchValue) {
      this.searchValue = newSearchValue
    },
  },
}
</script>

<style scoped lang="scss">
.categories-select {
  // used to display placeholder when input do not have the focus
  // known issue when using v-combobox and chips https://github.com/vuetifyjs/vuetify/issues/11553
  :deep(input) {
    max-height: inherit !important;
    padding: 0;
  }
}
</style>
